import type { SD849Status } from '@erp/rpc';

export function getStatusBadgeClassName(status: string): string {
  switch (status) {
    case 'accepted':
      return 'text-emerald-700 border-emerald-300 bg-emerald-50';
    case 'rejected':
      return 'text-rose-700 border-rose-300 bg-rose-50';
    case 'partial':
      return 'text-amber-600 border-amber-200 bg-amber-50';
    case 'resubmitted':
      return 'text-indigo-700 border-indigo-300 bg-indigo-50';
    // No decision, but the supplier asked us something — a question awaiting a
    // reply, not a failed parse. Deliberately distinct from `unknown` (no color).
    case 'advisory':
      return 'text-sky-700 border-sky-300 bg-sky-50';
    default:
      return '';
  }
}

export function getStatusFilterActiveClassName(status: string): string {
  switch (status) {
    case 'accepted':
      return 'bg-emerald-100 text-emerald-700 border-emerald-300';
    case 'rejected':
      return 'bg-rose-100 text-rose-700 border-rose-300';
    case 'partial':
      return 'bg-amber-100 text-amber-700 border-amber-300';
    case 'resubmitted':
      return 'bg-indigo-100 text-indigo-700 border-indigo-300';
    case 'advisory':
      return 'bg-sky-100 text-sky-700 border-sky-300';
    default:
      return 'bg-primary text-primary-foreground border-primary';
  }
}

/**
 * Badge colors for the CLAIM status — what we're doing about the claim — as
 * shown in the responses list. Deliberately a different palette from the
 * line-results rollup beside it: the two sit in the same row, and matching
 * colors would read as one repeated fact rather than two.
 *
 * A map rather than a third `switch` in this file, and keyed so an unmapped
 * (e.g. newly added) status renders plain rather than throwing.
 */
const CLAIM_STATUS_BADGE_CLASSES: Record<SD849Status, string> = {
  needs_review: 'text-orange-700 border-orange-300 bg-orange-50',
  waiting_on_supplier: 'text-blue-700 border-blue-300 bg-blue-50',
  resubmitted: 'text-violet-700 border-violet-300 bg-violet-50',
  needs_more_data: 'text-fuchsia-700 border-fuchsia-300 bg-fuchsia-50',
  complete: 'text-teal-700 border-teal-300 bg-teal-50',
  closed: 'text-slate-600 border-slate-300 bg-slate-100',
};

export function getClaimStatusBadgeClassName(status: SD849Status): string {
  return CLAIM_STATUS_BADGE_CLASSES[status] ?? '';
}
